import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuthStore } from '@/store/auth-store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SectionHeader } from '@/components/layout/SectionHeader';
import { toast } from 'sonner';
import { getOpenAIToken, saveOpenAIToken, deleteOpenAIToken } from '@/lib/openai-token-api';
import { changePassword } from '@/lib/auth-api';
import UsersTab from './components/UsersTab';

export default function SettingsPage() {
  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.role === 'ADMIN';

  // OpenAI token state
  const [openaiConnected, setOpenAIConnected] = useState(false);
  const [openaiEmail, setOpenAIEmail] = useState<string | null>(null);
  const [tokenInput, setTokenInput] = useState('');
  const [showTokenInput, setShowTokenInput] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  useEffect(() => {
    async function checkToken() {
      try {
        setIsLoading(true);
        const response = await getOpenAIToken();

        if (response.success && response.data.token) {
          setOpenAIConnected(true);

          try {
            const parts = response.data.token.split('.');
            if (parts.length === 3) {
              const payload = JSON.parse(atob(parts[1]));
              const email = payload.email || payload?.['https://api.openai.com/profile']?.email || null;
              setOpenAIEmail(email || 'Connected');
            } else {
              setOpenAIEmail('Connected');
            }
          } catch {
            setOpenAIEmail('Connected');
          }
        }
      } catch (error: any) {
        if (error?.response?.status !== 404) {
          console.error('Failed to load OpenAI token:', error);
        }
      } finally {
        setIsLoading(false);
      }
    }

    if (user) {
      checkToken();
    }
  }, [user]);

  const handleSaveToken = async () => {
    if (!tokenInput.trim()) {
      toast.error('Token is required');
      return;
    }

    setIsLoading(true);

    try {
      await saveOpenAIToken(tokenInput.trim());

      try {
        const parts = tokenInput.trim().split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]));
          const email = payload.email || payload?.['https://api.openai.com/profile']?.email || null;
          setOpenAIEmail(email || 'Connected');
        } else {
          setOpenAIEmail('Connected');
        }
      } catch {
        setOpenAIEmail('Connected');
      }

      setOpenAIConnected(true);
      setTokenInput('');
      setShowTokenInput(false);
      toast.success('OpenAI token saved to database');
    } catch (error: any) {
      console.error('Failed to save token:', error);
      toast.error(error?.message || 'Failed to save token');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenAIDisconnect = async () => {
    setIsLoading(true);

    try {
      await deleteOpenAIToken();

      setOpenAIConnected(false);
      setOpenAIEmail(null);
      toast.success('OpenAI token removed from database');
    } catch (error: any) {
      console.error('Failed to delete token:', error);
      toast.error(error?.message || 'Failed to delete token');
    } finally {
      setIsLoading(false);
    }
  };

  const passwordMismatch =
    confirmNewPassword.length > 0 && newPassword !== confirmNewPassword;

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmNewPassword) {
      toast.error('All password fields are required');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      toast.error('New password confirmation does not match');
      return;
    }

    setIsChangingPassword(true);

    try {
      const response = await changePassword({
        currentPassword,
        newPassword,
      });

      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      toast.success(response.message || 'Password changed successfully');
    } catch (error: any) {
      toast.error(error?.response?.data?.error?.message || 'Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Settings Control Center"
        subtitle="Configure account profile, security posture, and AI platform integrations."
        metric={openaiConnected ? 'Integration Connected' : 'Integration Pending'}
      />

      <Tabs defaultValue="profile">
        <TabsList className={isAdmin ? 'grid w-full grid-cols-4' : 'grid w-full grid-cols-3'}>
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
          <TabsTrigger value="integrations">Integrations</TabsTrigger>
          {isAdmin && <TabsTrigger value="users">User Management</TabsTrigger>}
        </TabsList>

        {/* ── Profile ── */}
        <TabsContent value="profile" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Account Profile</CardTitle>
              <CardDescription>Your account information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium">Username</p>
                  <p className="text-sm text-muted-foreground">{user?.username || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium">Name</p>
                  <p className="text-sm text-muted-foreground">
                    {user?.firstName && user?.lastName
                      ? `${user.firstName} ${user.lastName}`
                      : user?.firstName || user?.lastName || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium">Email</p>
                  <p className="text-sm text-muted-foreground">{user?.email || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium">Role</p>
                  <p className="text-sm text-muted-foreground">{user?.role || 'N/A'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Security ── */}
        <TabsContent value="security" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Security</CardTitle>
              <CardDescription>Session and access hardening controls</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded-lg border bg-background/70 p-3">
                  <p className="text-sm font-medium">Current Role</p>
                  <p className="text-sm text-muted-foreground">{user?.role || 'Unknown'}</p>
                </div>
                <div className="rounded-lg border bg-background/70 p-3">
                  <p className="text-sm font-medium">Two-Factor Authentication</p>
                  <p className="text-sm text-muted-foreground">Planned for next release</p>
                </div>
              </div>
              <div className="rounded-lg border bg-background/70 p-4 space-y-4">
                <div>
                  <p className="text-sm font-medium">Change Password</p>
                  <p className="text-xs text-muted-foreground">
                    Update your password without ending the current session.
                  </p>
                </div>
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="current-password">Current Password</Label>
                    <Input
                      id="current-password"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      autoComplete="current-password"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-password">New Password</Label>
                    <Input
                      id="new-password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-new-password">Confirm New Password</Label>
                    <Input
                      id="confirm-new-password"
                      type="password"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                  </div>
                </div>
                {passwordMismatch && (
                  <p className="text-xs text-destructive">New password confirmation does not match</p>
                )}
                <Button
                  onClick={handleChangePassword}
                  disabled={isChangingPassword || passwordMismatch}
                >
                  {isChangingPassword ? 'Updating...' : 'Change Password'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Security policies are centrally managed. Contact system administrator for role or access changes.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Integrations ── */}
        <TabsContent value="integrations" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Integrations</CardTitle>
              <CardDescription>Connect external services for automation and AI workflows</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg border bg-background/70 p-4">
                <p className="text-sm font-medium">OpenAI Integration</p>
                <p className="text-xs text-muted-foreground">
                  Enable AI-powered pipeline generation by linking your OpenAI access token.
                </p>
              </div>

              {openaiConnected ? (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Status</p>
                    <p className="text-sm text-muted-foreground">Connected as {openaiEmail || 'user'}</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleOpenAIDisconnect}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Processing...' : 'Disconnect'}
                  </Button>
                </div>
              ) : showTokenInput ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="openai-token">OpenAI Access Token</Label>
                    <Input
                      id="openai-token"
                      type="password"
                      value={tokenInput}
                      onChange={(e) => setTokenInput(e.target.value)}
                      placeholder="Paste your OpenAI access token here"
                    />
                    <p className="text-xs text-muted-foreground">
                      Get your token from Codex CLI by running <code className="bg-muted px-1 rounded">codex</code> in terminal,
                      then check <code className="bg-muted px-1 rounded">~/.codex/session.json</code>
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleSaveToken} disabled={isLoading}>
                      {isLoading ? 'Saving...' : 'Save Token'}
                    </Button>
                    <Button variant="outline" onClick={() => setShowTokenInput(false)} disabled={isLoading}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center space-y-4">
                  <p className="text-sm font-medium mb-4">
                    Connect your OpenAI account to enable AI pipeline generation
                  </p>
                  <div className="bg-muted p-4 rounded-lg text-left text-sm space-y-2">
                    <p className="font-medium">How to get your token:</p>
                    <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                      <li>Run <code className="bg-background px-1 rounded">codex</code> in your terminal</li>
                      <li>Complete the OAuth login</li>
                      <li>Check <code className="bg-background px-1 rounded">~/.codex/session.json</code></li>
                      <li>Copy the access_token value</li>
                    </ol>
                  </div>
                  <Button onClick={() => setShowTokenInput(true)} className="w-full">
                    Enter Token Manually
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── User Management (ADMIN only) ── */}
        {isAdmin && (
          <TabsContent value="users" className="mt-6">
            <UsersTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
